import React, { useContext, useEffect, useState } from "react";
import Header from "../components/Header";
import FormFlyware from "../components/FormFlyware";
import LinkOptionContext from "../contexts/LinkOptionContext";
import { languageMessages } from "traductor/FirstPayment";
import Skeleton from "../components/Skeleton";
import HTMLRenderer from "components/GlobalHTMLParser";
import LanguageContext from "contexts/LanguageContext";
import FormPersonalData from "components/FormPersonalData";
import PaymentConfirmation from "./PaymentConfirmation";
import Layout from "Layout";
import { REACT_APP_TIME_REFRESH, REACT_APP_COUNTRY_DEPLOY } from "config";
import { useSessionData } from "../hooks/useSessionData";
import { useFlywireHandler } from "../hooks/useFlywireHandler";
import { usePageIsScrollable } from "../hooks/usePageIsScrollable";
import ErrorDialog from "components/ErrorDialog";
import { omit } from "services/subscription/subscriptionService";
import PageInterview from "./PaymentInterview";
import { getQuotation } from "services/quotation/quotationService";
import { Promotion, QuotationResult } from "services/quotation/types";
import {
  getDisplayConfig,
  DisplayConfig,
} from "services/displayConfig/displayConfigService";
//import { StripeWrapper } from 'components/StripeWrapper';
//import FormStripe from 'components/FormStripe';
//import { Popup } from '@ole/react-library-agents';
import { useStripeHandler } from "hooks/useStripeHandler";
import { useProvider } from "hooks/useProvider";
import FirstPaymentBroker from "./FirstPaymentBroker";
import FormAuthorizeNet from "../components/FormAuthorizeNet";
import { useAuthorizeNetHandler } from "../hooks/useAuthorizeNetHandler";
import { OMITTED_STATUS, resolveConfirmationType } from "services/payment/confirmation";

interface FirstPaymentProps {
  language: string;
  region: string;
}

function FirstPayment({ language, region }: FirstPaymentProps) {
  const { changeLanguage } = useContext(LanguageContext);

  const [selectedLinkOption, setSelectedLinkOption] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [retry, setRetry] = useState(-1);
  const [success, setSuccess] = useState(false);
  const [status, setStatus] = useState<string>();
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const [isBrokerVariant, setIsBrokerVariant] = useState(false);
  const [displayConfig, setDisplayConfig] = useState<DisplayConfig | null>(
    null,
  );
  const [quotationReady, setQuotationReady] = useState(false);

  const { getProvider } = useProvider();

  const {
    token,
    sessionData,
    objectData,
    isLoaded: sessionLoaded,
    isVantage,
    error,
  } = useSessionData(language, region, changeLanguage);

  const isCardRegistration = sessionData?.action === "card_registration";
  const isPageScrollable = usePageIsScrollable([objectData, isCardRegistration, promotion, sessionLoaded, quotationReady]);

  useEffect(() => {
    if (error) {
      setIsDialogOpen(true);
    }
  }, [error]);

  useEffect(() => {
    if (!objectData) return;

    if (objectData.companyCode !== "BR" || !objectData.idQuotation) {
      setQuotationReady(true);
      return;
    }

    getQuotation(objectData.idQuotation).then(
      async (result: QuotationResult) => {
        if ("qualified" in result) {
          const partnerCode = result.partner_code?.trim();

          if (partnerCode === "nomad") setIsBrokerVariant(true);
          if (result.qualified) setPromotion(result);

          if (partnerCode) {
            const config = await getDisplayConfig(partnerCode);
            if (config) setDisplayConfig(config);
          }
        }
        setQuotationReady(true);
      },
    );
  }, [objectData]);

  const handleError = (type: string) => {
    setIsDialogOpen(true);
    console.error(type);
  };

  const { handleFlywireResponse, result: flywireResult } = useFlywireHandler({
    language,
    objectData,
    region,
    onError: (type) => {
      handleError(type);
    },
    setSuccess,
    setStatus,
  });

  const { isLoaded: stripeLoaded } = useStripeHandler(
    language,
    objectData,
    region,
    (type: string) => {
      handleError(type);
    },
    setStatus,
    setSuccess,
  );

  const {
    formToken: authnetFormToken,
    isLoaded: authnetLoaded,
    startAuthorizeNet,
    closeAuthorizeNet,
  } = useAuthorizeNetHandler({
    objectData,
    region,
    sessionToken: token,
    onError: (type) => handleError(type),
    setStatus,
    setSuccess,
  });

  const goPassarel = (region: string) => {
    const provider = getProvider(region, objectData?.gateway);
    if (provider === "authnet") {
      startAuthorizeNet();
      return;
    }
    callFormPayment();
  };

  const callFormPayment = () => {
    setRetry((r) => r + 1);
  };

  const changeOption = (newOption: React.SetStateAction<string>) => {
    setSelectedLinkOption(newOption);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleButtonClick = () => {
    window.location.reload();
  };

  const onReturn = () => {
    if (isVantage) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage("session-back");
      return;
    }
    window.open(objectData?.redirectUrl, "_self");
  };

  const onComplete = async () => {
    const res = await omit(
      token,
      objectData.idApplication,
      objectData.idQuotation,
      objectData.country,
      region,
    );
    if (!res.success) {
      setIsDialogOpen(true);
      console.error("omit", JSON.stringify(res.error));
    } else {
      setSuccess(true);
      setStatus(OMITTED_STATUS);
    }
  };

  const onInactive = () => {
    console.warn("activity", "Inactive session");
    window.location.reload();
  };

  const buildTextButton = (region: string, automaticApproval: boolean) => {
    if (region === "BR") {
      return languageMessages(language, "payNowAprroved");
    } else {
      return automaticApproval
        ? languageMessages(language, "payNowAprroved")
        : languageMessages(language, "payNowRegular");
    }
  };

  return (
    <Layout
      inactivityTime={Number.parseInt(REACT_APP_TIME_REFRESH)}
      onInactive={onInactive}
    >
      <LinkOptionContext.Provider
        value={{ option: selectedLinkOption, changeOption }}
      >
        {success ? (
          <>
            {objectData?.needInterview ? (
              <PageInterview
                language={language}
                name={`${objectData?.firstName} ${objectData?.lastName}`}
                firstName={objectData?.firstName}
                email={objectData?.email}
                phone={objectData?.phone}
                type={objectData?.typeIntervew}
                clientId={objectData?.clientId}
                idApplication={objectData?.idApplication}
              />
            ) : (
              <PaymentConfirmation
                language={language}
                tipo={resolveConfirmationType({
                  market: REACT_APP_COUNTRY_DEPLOY,
                  automaticApproval: Boolean(objectData?.automaticApproval),
                  status,
                  paymentMethod: flywireResult?.paymentMethod,
                })}
                field1={objectData?.email}
                isCardRegistration={isCardRegistration}
              />
            )}
          </>
        ) : (
          <>
            <ErrorDialog
              isOpen={isDialogOpen}
              error={error}
              onClose={handleClose}
              onButtonClick={handleButtonClick}
            />
            {sessionLoaded && quotationReady ? (
              isBrokerVariant ? (
                <FirstPaymentBroker
                  language={language}
                  objectData={objectData}
                  sessionData={sessionData}
                  promotion={promotion}
                  displayConfig={displayConfig ?? undefined}
                  hideToggle={true}
                  onClick={() => goPassarel(region)}
                  onComplete={onComplete}
                />
              ) : (
                <div
                  className={`app sticky-footer-layout${
                    isPageScrollable ? " is-scrollable" : ""
                  }`}
                >
                  <Header
                    paymentType="first"
                    language={language}
                    showLogo={true}
                  />
                  <div className="app-body">
                    <div className="img">
                      <div className="return">
                        <div onClick={onReturn}>{/* icono SVG */}</div>
                      </div>
                      <img
                        src="../icon_first_payment.png"
                        alt="First Payment"
                        style={{
                          margin: "0 auto",
                          display: objectData?.automaticApproval
                            ? "block"
                            : "none",
                        }}
                      />
                    </div>
                    <div className="text">
                      <div
                        className="title"
                        style={{
                          paddingBottom: objectData?.automaticApproval
                            ? "4px"
                            : "32px",
                        }}
                      >
                        {isCardRegistration
                          ? languageMessages(language, "promotionHeadline")
                          : objectData?.automaticApproval
                            ? languageMessages(language, "titleApproved")
                            : languageMessages(language, "titleRegular")}
                      </div>
                      <div className="subtitle">
                        {isCardRegistration ? (
                          promotion ? (
                            <span>
                              {languageMessages(
                                language,
                                "promotionSubtextBefore",
                              )}{" "}
                              <strong style={{ color: "#00D090" }}>
                                {promotion.free_installments}{" "}
                                {promotion.free_installments === 1
                                  ? languageMessages(
                                      language,
                                      "freeInstallmentsMonthSingular",
                                    )
                                  : languageMessages(
                                      language,
                                      "freeInstallmentsMonthPlural",
                                    )}{" "}
                                {languageMessages(
                                  language,
                                  "promotionSubtextAfter",
                                )}
                              </strong>
                            </span>
                          ) : (
                            languageMessages(
                              language,
                              "cardRegistrationSubtitle",
                            )
                          )
                        ) : (
                          <HTMLRenderer
                            html={
                              objectData?.automaticApproval
                                ? languageMessages(language, "subtitleApproved")
                                : languageMessages(language, "subtitleRegular")
                            }
                          />
                        )}
                      </div>
                    </div>
                    <div className="paymentInfo">
                      <div className="description">
                        {isCardRegistration
                          ? languageMessages(language, "premiumLabel")
                          : languageMessages(language, "description")}
                      </div>
                      <div className="amount">
                        {objectData?.currency} {objectData?.currencySymbol}
                        {sessionData?.amount}/
                        <span>
                          {isCardRegistration
                            ? languageMessages(language, "promotionMonthly")
                            : languageMessages(language, "frequency")[
                                objectData?.paymentFrequency
                              ]}
                        </span>
                      </div>
                    </div>
                    {objectData && (
                      <FormPersonalData
                        objectData={objectData}
                        //onClick={goPassarel(objectData?.provider)}
                        onClick={() => goPassarel(region)}
                        language={language}
                        buttonText={
                          isCardRegistration
                            ? languageMessages(language, "saveCard")
                            : buildTextButton(
                                region,
                                objectData?.automaticApproval,
                              )
                        }
                        showRecurrent
                        showPaymentMethodTitle={isCardRegistration}
                        isFirstPayment
                        belowButtonContent={
                          objectData?.showOmitPayment && (
                            <div className="skip">
                              <span onClick={onComplete}>
                                {languageMessages(language, "skip")}
                              </span>
                            </div>
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              )
            ) : (
              <Skeleton />
            )}
            {(stripeLoaded || (authnetLoaded && !authnetFormToken)) && <Skeleton />}
            <FormFlyware
              objectData={objectData}
              region={region}
              isModal={true}
              retry={retry}
              session={token}
              onCompleteFlywire={(dato: any) =>
                handleFlywireResponse(dato, token)
              }
              isCardRegistration={isCardRegistration}
            />
            <FormAuthorizeNet
              formToken={authnetFormToken}
              onClose={closeAuthorizeNet}
            />
            {/*openPopupStripe && <Popup
                width={643}
                paddingBottom={60}
                onClose={() => setOpenPopupStripe(false)}>
                  <StripeWrapper>
                    <FormStripe
                      email={objectData?.email}
                      name={objectData?.firstName + ' ' + objectData?.lastName}
                      region={region}
                      onlyTokenize={false}
                      session={token || '' }
                      onCompleteStripe={(dato:any, provider:string) => handleStripeResponse(dato, token)}
                      retry={() => setRetry((r) => r + 1)}
                      isModal={true}
                      url="session"
                      idApplication={objectData?.idApplication || ''}
                      idQuotation={objectData?.idQuotation || ''}
                      payingForPolicy={objectData?.payingForPolicy || ''}
                      payment_installments={objectData?.paymentIds?.join(',') || ''}
                    />
                  </StripeWrapper>
              </Popup>*/}
          </>
        )}
      </LinkOptionContext.Provider>
    </Layout>
  );
}

export default FirstPayment;
